import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate, Link } from 'react-router-dom';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useOpenRequestModal } from 'src/features/requests/request-form/use-open-request-modal';
import { cn } from 'src/lib/utils';
import { appRoutes } from 'src/routes';
import { If } from 'src/components/If';
import { Button } from 'src/components/ui/button/button';
import { ChevronLeft, FileVideo2, Video } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { PageHeading, PageHeadingTitle } from 'src/components/ui/page-heading';
import { useRequestsCounts } from 'src/models';
import { UnreadMessagesBadge } from 'src/components/ui/unread-messages-badge';
import { WebViewAuthService } from 'src/services/Auth/WebView.auth';

const RequestDefaultPageContentTop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, title, status, canEdit } = useDecoratedRequestContext();
  const { openRequestModal } = useOpenRequestModal();
  const { counts } = useRequestsCounts();

  // Check if this request has unread messages across all status groups
  const hasUnreadMessages = React.useMemo(() => {
    if (!counts || !id) return false;

    return Object.values(counts).some((statusCounts) => statusCounts.unread_messages?.includes(id));
  }, [counts, id]);

  return (
    <div
      ref={ref}
      className={cn('tw-flex tw-flex-wrap  tw-items-center tw-gap-4', className)}
      {...props}
    >
      {children || (
        <>
          <PageHeading>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Button
                    variant={'unset'}
                    size={'iconXs'}
                    onClick={() => {
                      if (location.key !== 'default') {
                        history.back();
                      } else {
                        navigate(appRoutes.dashboard);
                      }
                    }}
                  >
                    <ChevronLeft className={'tw-size-4'} />
                  </Button>
                  {!WebViewAuthService.isWebViewAuthEnabled() && (
                    <BreadcrumbLink asChild>
                      <Link to={appRoutes.dashboard}>
                        {t('common:dashboard', {
                          defaultValue: 'Dashboard',
                        })}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!WebViewAuthService.isWebViewAuthEnabled() && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={appRoutes.requestsList}>
                      {t('common:requests', {
                        defaultValue: 'Requests',
                      })}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbPage>
                  {t('common:request', {
                    defaultValue: 'Request',
                  })}
                </BreadcrumbPage>
              </BreadcrumbList>
            </Breadcrumb>
            <PageHeadingTitle className={'tw-relative tw-flex-nowrap'}>
              <UnreadMessagesBadge
                show={hasUnreadMessages}
                position="tw-left-0 tw-top-0"
                tooltipText={t('common:unread_messages', { defaultValue: 'Unread messages' })}
              />
              <div>
                <Video className={'tw-size-6'} />
              </div>
              <span className={'tw-line-clamp-1'}>
                {title || t('common:request', { defaultValue: 'Request' })}
              </span>
            </PageHeadingTitle>
          </PageHeading>

          <div className={'tw-ms-auto tw-flex tw-gap-2'}>
            <If
              when={canEdit}
              else={
                <Button asChild type={'button'} variant={'outline'} size={'responsive'}>
                  <Link to={generatePath(appRoutes.requestRevisions, { requestId: id })}>
                    <FileVideo2 size={20} />
                    <span className={'tw-ms-3'}>{t('models/revision:name.plural')}</span>
                  </Link>
                </Button>
              }
            >
              <Button
                type={'button'}
                variant={'brand'}
                size={'responsive'}
                onClick={async () => {
                  await openRequestModal(id);
                }}
              >
                {t('pages/space:forms.request.edit')}
              </Button>
            </If>
          </div>
        </>
      )}
    </div>
  );
});
RequestDefaultPageContentTop.displayName = 'RequestDefaultPageContentTop';

export { RequestDefaultPageContentTop };
